import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStop, faRedo, faSearch, faMicrophone, faMicrophoneSlash, faTrash } from "@fortawesome/free-solid-svg-icons";
import useAudioRecorder from "../../components/ChatBot/useAudioRecorder";
import { ModePicker } from "./ModePicker";

const CHATBOT_API_URL = "https://stream.gizmott.com/chat-agent";
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
let _chatUid = 0;
const chatUid = () => ++_chatUid;

export default function ChatPanel({ mode, setMode }) {
  const [messages, setMessages]                     = useState([]);
  const [input, setInput]                           = useState("");
  const [isTyping, setIsTyping]                     = useState(false);
  const [lastQuestion, setLastQuestion]             = useState("");
  const [conversationId, setConversationId]         = useState(null);
  const [suggestions, setSuggestions]               = useState([]);
  const [showSuggestions, setShowSuggestions]       = useState(false);
  const [suppressSuggestions, setSuppressSuggestions] = useState(false);
  const [selectedSuggIdx, setSelectedSuggIdx]       = useState(-1);
  const [pendingAction, setPendingAction]           = useState(null);
  const [pendingEmail, setPendingEmail]             = useState("");
  const [interruptInputs, setInterruptInputs]       = useState({});
  const [emailSubmitted, setEmailSubmitted]         = useState(false);
  const [emailConfirmed, setEmailConfirmed]         = useState(false);
  const [emailError, setEmailError]                 = useState({});

  const { isRecording, startRecording, stopRecording } = useAudioRecorder(text => setInput(text));

  const accumulatedRef = useRef("");
  const pendingBotRef  = useRef("");
  const actionRef      = useRef(null);
  const messagesEndRef = useRef(null);
  const controllerRef  = useRef(null);
  const msgIdRef       = useRef(0);
  const inputContRef   = useRef(null);
  const inputRef       = useRef(null);

  const newId = () => { msgIdRef.current += 1; return msgIdRef.current; };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handler = e => {
      if (inputContRef.current && !inputContRef.current.contains(e.target))
        setShowSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const stopGenerating = () => {
    if (!controllerRef.current) return;
    controllerRef.current.abort();
    controllerRef.current = null;
    setIsTyping(false);
    setMessages(prev => prev.map((m, i) =>
      i === prev.length - 1 && m.sender === "bot" && m.isStreaming
        ? { ...m, isStreaming: false, interrupted: true } : m
    ));
  };

  const retryLastQuestion = () => {
    if (!lastQuestion) return;
    const lastBot = [...messages].reverse().find(m => m.sender === "bot");
    if (lastBot) sendMessage(lastQuestion, messages, lastBot.id);
  };

  const handleClearChat = async () => {
    setMessages([]);
    setLastQuestion("");
    setPendingAction(null);
    setPendingEmail("");
    setInterruptInputs({});
    setEmailSubmitted(false);
    setEmailConfirmed(false);
    if (conversationId) {
      try {
        await axios.post(`${CHATBOT_API_URL}/clear-context`, null, {
          params: { conversation_id: conversationId },
          headers: { "Content-Type": "application/json" },
        });
      } catch (_) {}
    }
    setConversationId(null);
  };

  const handleResume = async (interrupt_id, user_input, email) => {
    if (!interrupt_id || !user_input) return;
    try {
      setMessages(prev =>
        prev.map(m => m.interrupt_id === interrupt_id ? { ...m, buttonsDisabled: true } : m)
      );
      setMessages(prev => [...prev, { id: newId(), text: user_input, sender: "user" }]);

      const res = await fetch(`${CHATBOT_API_URL}/resume`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "access-token": localStorage.getItem("access-token"),
          role_id: localStorage.getItem("role_id"),
          pubid: localStorage.getItem("pub_id"),
        },
        body: JSON.stringify({ interrupt_id, user_input, email }),
      });
      if (!res.ok) throw new Error(`Resume failed: ${res.status}`);

      if (!res.body) {
        const json = await res.json().catch(() => null);
        if (json?.interrupt) {
          setMessages(prev => [...prev, {
            id: newId(), text: json.message || json.content || "",
            sender: "bot", type: "interrupt",
            interrupt_id: json.interrupt_id,
            waiting_for_email: json.waiting_for_email || false,
            email: json.email || "",
          }]);
        } else if (json?.text) {
          setMessages(prev => [...prev, { id: newId(), text: json.text, sender: "bot" }]);
        }
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        while (buf.includes("\n\n")) {
          const idx = buf.indexOf("\n\n");
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const m = chunk.match(/data:(.*)/s);
          if (!m) continue;
          let raw = m[1].trim();
          if (raw === "[DONE]") continue;
          let data;
          try { data = JSON.parse(raw); } catch (_) { continue; }

          if (data.interrupt || data.type === "interrupt") {
            const im = {
              id: newId(), text: data.message || data.content || "",
              sender: "bot", type: "interrupt",
              interrupt_id: data.interrupt_id || interrupt_id,
              waiting_for_email: data.waiting_for_email || false,
              email: data.email || email || "",
            };
            setMessages(prev => [...prev, im]);
            if (im.waiting_for_email && data.email)
              setInterruptInputs(prev => ({ ...prev, [im.interrupt_id]: data.email }));
            reader.cancel();
            return;
          }
          if (data.type === "token") {
            const textChunk = data.content.replace(/\\n/g, "\n");
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last?.sender === "bot" && last.isStreaming)
                return prev.map((m, i) => i === prev.length - 1 ? { ...m, text: m.text + textChunk } : m);
              return [...prev, { id: newId(), text: textChunk, sender: "bot", isStreaming: true }];
            });
          }
          if (data.type === "done") {
            setMessages(prev => {
              const upd = [...prev];
              const li = upd.length - 1;
              if (li >= 0 && upd[li].sender === "bot" && upd[li].isStreaming)
                upd[li] = { ...upd[li], isStreaming: false };
              return upd;
            });
          }
        }
      }
    } catch (err) {
      console.error("handleResume error:", err);
    }
  };

  const sendMessage = async (messageText, prevMessages, replaceId = null) => {
    setIsTyping(true);
    accumulatedRef.current = "";
    pendingBotRef.current  = "";
    actionRef.current      = null;
    try {
      if (controllerRef.current) controllerRef.current.abort();
      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      const pubid = localStorage.getItem("pub_id");
      const currentConvId = conversationId || (() => {
        const nid = `${pubid}_${Math.floor(Math.random() * 100000) + 1}`;
        setConversationId(nid);
        return nid;
      })();

      const response = await fetch(`${CHATBOT_API_URL}/support`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "access-token": localStorage.getItem("access-token"),
          role_id: localStorage.getItem("role_id"),
          pubid: localStorage.getItem("pub_id"),
        },
        body: JSON.stringify({ message: messageText, pubid, conversation_id: currentConvId }),
        signal,
      });
      if (!response.ok) throw new Error("Network error");

      const reader = response.body.getReader();
      let buf = "";
      const isReplacing = replaceId !== null;
      let replText = "";
      let tokenCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += new TextDecoder().decode(value);
        while (buf.includes("\n\n")) {
          const end = buf.indexOf("\n\n");
          const eventText = buf.slice(0, end);
          buf = buf.slice(end + 2);
          const dm = eventText.match(/data: (.*)/);
          if (!dm) continue;
          const data = JSON.parse(dm[1]);

          if (data.type === "token") {
            const tc = data.content.trim().toLowerCase();
            accumulatedRef.current += tc;
            const norm = accumulatedRef.current.replace(/\s+/g, "");
            if (norm.includes("password_reset") || (norm.includes("password") && norm.includes("reset"))) {
              actionRef.current = "password_reset";
              continue;
            }
            if (actionRef.current) { pendingBotRef.current += data.content; continue; }
            tokenCount++;
            if (tokenCount > 4096) { stopGenerating(); break; }
            const processed = data.content.replace(/\\n/g, "\n");
            if (isReplacing) {
              replText += processed;
              setMessages(prev => prev.map(m => m.id === replaceId ? { ...m, text: replText, isStreaming: true } : m));
            } else {
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.sender === "bot" && last.isStreaming)
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, text: m.text + processed } : m);
                return [...prev, { id: newId(), text: processed, sender: "bot", isStreaming: true }];
              });
            }
          } else if (data.type === "interrupt") {
            const { interrupt_id, waiting_for_email, content } = data;
            const nid = newId();
            setMessages(prev => {
              const upd = prev.filter((m, i) => !(m.sender === "bot" && m.isStreaming && i === prev.length - 1));
              return [...upd, {
                id: nid, text: content || data.message || "",
                sender: "bot", type: "interrupt", interrupt_id, waiting_for_email,
              }];
            });
          } else if (data.type === "done") {
            if (actionRef.current === "password_reset") {
              setMessages(prev => {
                const filtered = prev.filter((m, i) => !(m.sender === "bot" && m.isStreaming && i === prev.length - 1));
                const existingEmail = prevMessages.find(m => m.sender === "user" && EMAIL_REGEX.test(m.text));
                if (existingEmail) { setPendingEmail(existingEmail.text); setPendingAction("confirm_email"); }
                else { setPendingAction("ask_email"); }
                return [...filtered, { id: newId(), text: "", sender: "bot", isStreaming: false, isDynamicUI: true }];
              });
            } else {
              setMessages(prev => {
                const upd = [...prev];
                const mi = isReplacing ? upd.findIndex(m => m.id === replaceId) : upd.length - 1;
                if (mi !== -1 && upd[mi]?.sender === "bot") upd[mi] = { ...upd[mi], isStreaming: false };
                return upd;
              });
            }
            if (data.action === "password_reset") {
              const existingEmail = prevMessages.find(m => m.sender === "user" && EMAIL_REGEX.test(m.text));
              if (existingEmail) { setPendingEmail(existingEmail.text); setPendingAction("confirm_email"); }
              else { setPendingAction("ask_email"); }
            } else if (data.action === "ask_email") {
              setPendingAction("ask_email");
            } else if (data.action === "confirm_email") {
              setPendingEmail(data.email || "");
              setPendingAction("confirm_email");
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setMessages(prev => [...prev, { id: newId(), text: "Error. Try again.", sender: "bot" }]);
      }
    } finally {
      setIsTyping(false);
      controllerRef.current = null;
    }
  };

  const debouncedSearch = useRef(
    debounce(searchTerm => {
      if (!searchTerm || searchTerm.trim().length < 3) {
        setSuggestions([]); setShowSuggestions(false); return;
      }
      axios.post(
        `${CHATBOT_API_URL}/suggestions`,
        { message: searchTerm },
        { headers: { "Content-Type": "application/json", pubid: localStorage.getItem("pub_id"), channelid: localStorage.getItem("currentChannel") } }
      ).then(res => {
        const data = res?.data?.suggestions || [];
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      }).catch(() => { setSuggestions([]); setShowSuggestions(false); });
    }, 500)
  ).current;

  const handleInputChange = e => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
    if (suppressSuggestions) setSuppressSuggestions(false);
    debouncedSearch(e.target.value);
  };

  const handleSelectSuggestion = question => {
    setInput(question);
    setShowSuggestions(false);
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;
    setSuppressSuggestions(true);
    const userMsg = { id: newId(), text: input, sender: "user" };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLastQuestion(input);
    setInput("");
    await sendMessage(input, newMessages, null);
  };

  const handleKeyDown = e => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggIdx(i => (i >= suggestions.length - 1 ? 0 : i + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggIdx(i => (i <= 0 ? suggestions.length - 1 : i - 1));
        return;
      }
      if (e.key === "Enter" && selectedSuggIdx >= 0) {
        e.preventDefault();
        const q = suggestions[selectedSuggIdx].question || suggestions[selectedSuggIdx];
        setInput("");
        setShowSuggestions(false);
        setTimeout(() => {
          const um = { id: newId(), text: q, sender: "user" };
          const nm = [...messages, um];
          setMessages(nm);
          setLastQuestion(q);
          sendMessage(q, nm, null);
        }, 100);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleEmailSubmit = () => {
    if (!pendingEmail.trim() || emailSubmitted) return;
    if (!EMAIL_REGEX.test(pendingEmail.trim())) { setEmailError({ submit: "Please enter a valid email address." }); return; }
    setEmailError({});
    setEmailSubmitted(true);
    const um = { id: newId(), text: pendingEmail, sender: "user" };
    setMessages(prev => [...prev, um]);
    sendMessage(pendingEmail, [...messages, um]);
  };

  const handleEmailConfirmYes = () => {
    if (!pendingEmail.trim() || emailConfirmed) return;
    if (!EMAIL_REGEX.test(pendingEmail.trim())) { setEmailError({ confirm: "Please enter a valid email address." }); return; }
    setEmailError({});
    setEmailConfirmed(true);
    const um = { id: newId(), text: `Yes, ${pendingEmail}`, sender: "user" };
    setMessages(prev => [...prev, um]);
    sendMessage(pendingEmail, [...messages, um]);
  };

  const handleEmailConfirmNo = () => {
    if (emailConfirmed) return;
    setEmailConfirmed(true);
    setPendingEmail("");
    setEmailSubmitted(false);
    setPendingAction("ask_email");
  };

  const handleEmailInput = (e, interruptId) => {
    const v = e.target.value;
    setInterruptInputs(prev => ({ ...prev, [interruptId]: v }));
    if (v && !EMAIL_REGEX.test(v.trim()))
      setEmailError(prev => ({ ...prev, [interruptId]: "Please enter a valid email address." }));
    else
      setEmailError(prev => ({ ...prev, [interruptId]: "" }));
  };
  return (
    <>
      <div style={SC.body}>
        {messages.length === 0 ? (
          <div style={SC.empty}>
            <div style={SC.emptyIcon}>💬</div>
            <p style={SC.emptyTitle}>AI Chat</p>
            <p style={SC.emptyDesc}>Ask me anything about the platform — I'll answer in real time.</p>
            <div style={SC.clearRow}>
              <button style={SC.clearBtn} onClick={handleClearChat} title="Clear chat">
                <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11, marginRight: 4 }} />
                Clear
              </button>
            </div>
          </div>
        ) : (
          <div style={SC.messageList}>
            <div style={{ ...SC.clearRow, marginBottom: 4 }}>
              <button style={SC.clearBtn} onClick={handleClearChat} title="Clear chat">
                <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11, marginRight: 4 }} />
                Clear chat
              </button>
            </div>

            {messages.map((msg, index) => {
              if (msg.type === "interrupt") {
                const waitEmail = Boolean(msg.waiting_for_email);
                const iid = msg.interrupt_id || `interrupt_${msg.id || index}`;
                const isDisabled = msg.buttonsDisabled || false;
                return (
                  <div key={msg.id} style={SC.interruptCard}>
                    <p style={SC.interruptText}>{msg.text}</p>
                    {waitEmail ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <input
                          type="email"
                          style={SC.emailInput}
                          placeholder="Enter your email"
                          value={interruptInputs[iid] || ""}
                          onChange={e => handleEmailInput(e, iid)}
                          disabled={isDisabled}
                          onKeyDown={e => {
                            if (e.key === "Enter" && !isDisabled) {
                              const v = (interruptInputs[iid] || "").trim();
                              if (v) handleResume(msg.interrupt_id, v, v);
                            }
                          }}
                        />
                        {emailError[iid] && <span style={SC.emailError}>{emailError[iid]}</span>}
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            style={SC.btnPrimary}
                            disabled={!(interruptInputs[iid] || "").trim() || isDisabled}
                            onClick={() => {
                              const v = (interruptInputs[iid] || "").trim();
                              if (v && EMAIL_REGEX.test(v)) handleResume(msg.interrupt_id, v, v);
                            }}
                          >Submit</button>
                          <button
                            style={SC.btnSecondary}
                            disabled={isDisabled}
                            onClick={() => {
                              setMessages(prev => prev.filter(m => m.interrupt_id !== msg.interrupt_id));
                              handleResume(msg.interrupt_id || iid, "no");
                              setPendingAction(null);
                              setPendingEmail("");
                            }}
                          >Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button style={SC.btnSuccess} disabled={isDisabled} onClick={() => handleResume(msg.interrupt_id, "yes", msg.email)}>Yes</button>
                        <button style={SC.btnDanger}  disabled={isDisabled} onClick={() => handleResume(msg.interrupt_id, "no")}>No</button>
                      </div>
                    )}
                  </div>
                );
              }
              if (msg.isDynamicUI && msg.sender === "bot") {
                const isLast = index === messages.length - 1;
                return (
                  <div key={msg.id}>
                    {isLast && pendingAction === "ask_email" && (
                      <div style={SC.dynamicCard}>
                        <p style={{ marginBottom: 8, fontSize: 13, color: "#1e293b" }}>Please enter your registered email address:</p>
                        <input
                          type="email"
                          style={SC.emailInput}
                          placeholder="Enter your email"
                          value={pendingEmail}
                          onChange={e => {
                            setPendingEmail(e.target.value);
                            if (e.target.value && !EMAIL_REGEX.test(e.target.value.trim()))
                              setEmailError({ submit: "Please enter a valid email address." });
                            else
                              setEmailError({});
                          }}
                          disabled={emailSubmitted}
                          onKeyDown={e => { if (e.key === "Enter" && !emailSubmitted) { e.preventDefault(); handleEmailSubmit(); } }}
                        />
                        {emailError.submit && <span style={SC.emailError}>{emailError.submit}</span>}
                        <button
                          style={{ ...SC.btnPrimary, marginTop: 8 }}
                          disabled={!pendingEmail.trim() || emailSubmitted || !!emailError.submit}
                          onClick={handleEmailSubmit}
                        >Submit</button>
                      </div>
                    )}
                    {isLast && pendingAction === "confirm_email" && (
                      <div style={SC.dynamicCard}>
                        <p style={{ marginBottom: 8, fontSize: 13, color: "#1e293b" }}>Is this your email: <strong>{pendingEmail}</strong>?</p>
                        {emailError.confirm && <span style={SC.emailError}>{emailError.confirm}</span>}
                        <div style={{ display: "flex", gap: 6 }}>
                          <button style={SC.btnSuccess} disabled={emailConfirmed || !!emailError.confirm} onClick={handleEmailConfirmYes}>Yes</button>
                          <button style={SC.btnDanger}  disabled={emailConfirmed} onClick={handleEmailConfirmNo}>No</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div key={msg.id} style={{
                  display: "flex", marginBottom: 6,
                  justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                  flexDirection: "column",
                  alignItems: msg.sender === "user" ? "flex-end" : "flex-start",
                }}>
                  <div style={{ ...SC.bubble, ...(msg.sender === "user" ? SC.userBubble : SC.botBubble) }}>
                    {msg.sender === "bot" ? (
                      <div style={SC.mdWrapper}>
                        <ReactMarkdown plugins={[gfm]} source={msg.text} linkTarget="_blank" />
                      </div>
                    ) : msg.text}
                  </div>
                  {msg.sender === "bot" && !msg.isStreaming && !msg.isDynamicUI &&
                    msg.id === messages[messages.length - 1]?.id && (
                    <button onClick={retryLastQuestion} style={SC.retryBtn} title="Try Again">
                      <FontAwesomeIcon icon={faRedo} style={{ fontSize: 10 }} /> Try Again
                    </button>
                  )}
                </div>
              );
            })}

            {isTyping && (
              <div style={SC.thinkingRow}>
                <span style={SC.dot} />
                <span style={{ ...SC.dot, animationDelay: "0.2s" }} />
                <span style={{ ...SC.dot, animationDelay: "0.4s" }} />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
      <div ref={inputContRef} style={SC.footer}>
        {showSuggestions && suggestions.length > 0 && (
          <div style={SC.suggestionsList}>
            {suggestions.slice(0, 3).map((item, idx) => (
              <button
                key={idx}
                style={{ ...SC.suggestionItem, ...(selectedSuggIdx === idx ? SC.suggestionItemSelected : {}) }}
                onMouseDown={() => handleSelectSuggestion(item.question || item)}
              >
                <FontAwesomeIcon icon={faSearch} style={{ fontSize: 10, color: "#94a3b8", marginRight: 6, flexShrink: 0 }} />
                {item.question || item}
              </button>
            ))}
          </div>
        )}
        <div style={SC.inputRow}>
          <textarea
            ref={inputRef}
            value={input}
            rows={1}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={isTyping ? "AI is typing…" : "Ask anything…"}
            style={SC.input}
          />
          <button
            onClick={isRecording ? stopRecording : startRecording}
            style={SC.iconBtn}
            title={isRecording ? "Stop recording" : "Start recording"}
          >
            <FontAwesomeIcon
              icon={isRecording ? faMicrophoneSlash : faMicrophone}
              style={{ fontSize: 13, color: isRecording ? "#ef4444" : "#64748b" }}
            />
          </button>
          {isTyping ? (
            <button onClick={stopGenerating} style={SC.iconBtn} title="Stop generating">
              <FontAwesomeIcon icon={faStop} style={{ fontSize: 13, color: "#6366f1" }} />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              style={{ ...SC.sendBtn, opacity: input.trim() ? 1 : 0.45 }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
            </button>
          )}
        </div>
        <ModePicker mode={mode} setMode={setMode} />
      </div>
    </>
  );
}
const SC = {
  body: {
    flex: 1, minHeight: 0, overflowY: "auto",
    scrollbarWidth: "thin", scrollbarColor: "rgba(0,0,0,0.12) transparent",
    background: "#f8fafc",
    display: "flex", flexDirection: "column",
  },
  empty: {
    textAlign: "center", margin: "auto", padding: "0 20px", color: "#64748b",
  },
  emptyIcon:  { fontSize: 32, marginBottom: 8 },
  emptyTitle: { margin: "0 0 6px", color: "#1e293b", fontWeight: 600, fontSize: 15 },
  emptyDesc:  { margin: "0 0 14px", fontSize: 13, lineHeight: 1.5, color: "#64748b" },
  clearRow:   { display: "flex", justifyContent: "center" },
  clearBtn: {
    background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
    color: "#94a3b8", fontSize: 11, cursor: "pointer",
    padding: "4px 10px", display: "flex", alignItems: "center",
  },
  messageList: {
    display: "flex", flexDirection: "column", gap: 2,
    padding: "12px 10px",
  },
  bubble: {
    padding: "9px 13px", borderRadius: 12,
    fontSize: 13, lineHeight: 1.5, wordBreak: "break-word",
    maxWidth: "88%",
  },
  userBubble: {
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    color: "#fff", borderBottomRightRadius: 4,
  },
  botBubble: {
    background: "#ffffff", border: "1px solid #e2e8f0",
    color: "#1e293b", borderBottomLeftRadius: 4,
  },
  mdWrapper: {
    fontSize: 13, lineHeight: 1.6,
  },
  retryBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: "#6366f1", fontSize: 11, padding: "2px 4px",
    marginTop: 3, display: "flex", alignItems: "center", gap: 4,
  },
  thinkingRow: {
    display: "flex", alignItems: "center", gap: 5,
    padding: "10px 14px", background: "#f1f5f9", borderRadius: 10,
    width: "fit-content",
  },
  dot: {
    display: "inline-block", width: 7, height: 7,
    borderRadius: "50%", background: "#6366f1",
    animation: "ai-think 1.2s infinite",
  },
  interruptCard: {
    background: "#fffbeb", border: "1px solid #fde68a",
    borderRadius: 10, padding: "12px 14px", marginBottom: 6,
  },
  interruptText: { fontWeight: 600, color: "#92400e", marginBottom: 10, fontSize: 13 },
  dynamicCard: {
    background: "#f8fafc", border: "1px solid #e2e8f0",
    borderRadius: 10, padding: "12px 14px", marginBottom: 6,
  },
  emailInput: {
    width: "100%", borderRadius: 8, border: "1px solid #cbd5e1",
    padding: "7px 10px", fontSize: 13, outline: "none",
    background: "#fff", color: "#1e293b", boxSizing: "border-box",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  emailError: { color: "#ef4444", fontSize: 12, marginTop: 4, display: "block" },
  btnPrimary: {
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff",
    border: "none", borderRadius: 8, padding: "6px 14px",
    fontSize: 12, cursor: "pointer", fontWeight: 500,
  },
  btnSecondary: {
    background: "#f1f5f9", color: "#475569",
    border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 14px",
    fontSize: 12, cursor: "pointer", fontWeight: 500,
  },
  btnSuccess: {
    background: "#22c55e", color: "#fff",
    border: "none", borderRadius: 8, padding: "6px 14px",
    fontSize: 12, cursor: "pointer", fontWeight: 500,
  },
  btnDanger: {
    background: "#ef4444", color: "#fff",
    border: "none", borderRadius: 8, padding: "6px 14px",
    fontSize: 12, cursor: "pointer", fontWeight: 500,
  },
  footer: {
    padding: "10px 12px", borderTop: "1px solid #e2e8f0",
    flexShrink: 0, background: "#ffffff", position: "relative",
  },
  suggestionsList: {
    position: "absolute", bottom: "100%", left: 12, right: 12,
    background: "#ffffff", border: "1px solid #e2e8f0",
    borderRadius: 10, boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
    overflow: "hidden", zIndex: 10,
  },
  suggestionItem: {
    width: "100%", display: "flex", alignItems: "center",
    padding: "9px 12px", background: "none", border: "none",
    borderBottom: "1px solid #f1f5f9",
    textAlign: "left", cursor: "pointer", fontSize: 12, color: "#334155",
  },
  suggestionItemSelected: { background: "rgba(99,102,241,0.06)", color: "#4f46e5" },
  inputRow: { display: "flex", gap: 6, alignItems: "flex-end" },
  input: {
    flex: 1, background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 10,
    color: "#1e293b", fontSize: 13, padding: "9px 12px",
    resize: "none", outline: "none", lineHeight: 1.5,
    minHeight: 38, maxHeight: 100, overflow: "hidden",
    transition: "border-color 0.15s",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  iconBtn: {
    width: 36, height: 36, borderRadius: 9, border: "1px solid #e2e8f0",
    background: "#f8fafc", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  sendBtn: {
    width: 36, height: 36, borderRadius: 9, border: "none",
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    color: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
};
